import { PluginData } from '@/components/admin/plugins/plugin.types';
import {
  MinervaPluginInterface,
  RegisterPluginProps,
  RegisterPluginResult,
} from '@/services/pluginsManager/pluginsManager.types';
import { getApiUrls } from '@/services/pluginsManager/project/data/getApiUrls';
import { ONE, ZERO } from '@/constants/common';
import { EventPayloadMap, Events, ModelElement, NewReaction, User } from 'minerva-plugin-api';
import { Configuration } from '@/types/models';

export const pluginData: Record<string, PluginData> = {};

function mockDebugLog(message: string): void {
  // eslint-disable-next-line no-console
  console.log(`[Plugin mock]${message}`);
}
function prepareConfiguration(): Configuration {
  return {
    elementTypes: [],
    annotators: [],
    bioEntityFields: [],
    buildDate: '',
    gitHash: '',
    options: [],
    imageFormats: [],
    miriamTypes: {},
    modelFormats: [],
    modificationStateTypes: {},
    overlayTypes: [],
    privilegeTypes: {},
    version: '',
    reactionTypes: [],
    unitTypes: [],
  };
}
export const PluginMock = {
  init(): void {
    const minerva: MinervaPluginInterface = {
      status: {
        getVersion() {
          return '';
        },
      },
      users: {
        getLoggedUser(): User | undefined {
          return undefined;
        },
      },
      elements: {
        data: {
          getAll: () => {
            return Promise.resolve([] as ModelElement[]);
          },
        },
      },
      reactions: {
        data: {
          getAll: () => {
            return Promise.resolve([] as NewReaction[]);
          },
        },
      },
      configuration: prepareConfiguration(),
      data: {
        bioEntities: {
          getAllBioEntities: () => {
            return [];
          },
          getAllChemicals: () => {
            return [];
          },
          getAllDrugs: () => {
            return [];
          },
          getAllMarkers: () => {
            return [];
          },
          getShownElements: () => {
            return {
              markers: [],
              chemicals: [],
              drugs: [],
              content: [],
            };
          },
          addSingleMarker: () => {
            throw new Error('Not implemented');
          },
          removeSingleMarker: () => {},
          removeAllMarkers: () => {},
          clearAllElements: () => {},
        },
      },
      interface: {
        getOpenedPanel: () => {
          return undefined;
        },
        hidePanel: () => {},
        openPanel: () => {},
      },
      map: {
        data: {
          getBounds: () => {
            return undefined;
          },
          getModels: () => {
            return [];
          },
          getOpenMapId: () => {
            return -ONE;
          },
        },
        fitBounds: () => {},
        getCenter: () => {
          return {
            x: ZERO,
            y: ZERO,
          };
        },
        getZoom: () => {
          return -ONE;
        },
        openMap: () => {},
        setCenter: () => {},
        setZoom: () => {},
        triggerSearch: () => {
          return Promise.reject();
        },
      },
      overlays: {
        addDataOverlay: () => {
          return Promise.reject();
        },
        data: {
          getDataOverlays: () => {
            return [];
          },
          getVisibleDataOverlays: () => {
            return [];
          },
        },
        hideDataOverlay: () => {},
        removeDataOverlay: () => {},
        showDataOverlay: () => {},
      },
      overviewImage: {
        getCurrentOverviewImage: () => {
          return undefined;
        },
        getOverviewImages: () => {
          return [];
        },
        hideOverviewImageModal: () => {},
        selectOverviewImage: () => {},
        showOverviewImageModal: () => {},
      },
      project: {
        data: {
          getApiUrls,
          getDisease: () => {
            return null;
          },
          getName: () => {
            return 'N/A';
          },
          getOrganism: () => {
            return null;
          },
          getProjectId: () => {
            return 'N/A';
          },
          getVersion: () => {
            return 'N/A';
          },
        },
      },
      plugins: {
        registerPlugin: PluginMock.registerPlugin,
      },
    };
    window.minerva = minerva;
  },
  registerPlugin(plugin: RegisterPluginProps): RegisterPluginResult {
    pluginData[plugin.pluginUrl] = {
      name: plugin.pluginName,
      version: plugin.pluginVersion,
      url: plugin.pluginUrl,
    };
    // eslint-disable-next-line no-console
    console.log('Registering plugin:', pluginData[plugin.pluginUrl]);
    return {
      element: document.createElement('div'),
      events: {
        addListener: <T extends Events>(
          type: T,
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          _callback: (data: EventPayloadMap[T]) => void,
        ): void => {
          mockDebugLog(`events.addListener; type: ${type}`);
        },
        removeListener: (): void => {
          mockDebugLog('events.removeListener');
        },
        removeAllListeners: (): void => {
          mockDebugLog('events.removeAllListeners');
        },
      },
      contextMenu: {
        addMenu: (): string => {
          mockDebugLog('contextMenu.addMenu');
          return '';
        },
        updateMenu: (): void => {
          mockDebugLog('contextMenu.updateMenu');
        },
        removeMenu: (): void => {
          mockDebugLog('contextMenu.removeMenu');
        },
      },
      legend: {
        setLegend: (): void => {
          mockDebugLog('legend.setLegend');
        },
        removeLegend: (): void => {
          mockDebugLog('legend.removeLegend');
        },
      },
    };
  },
  getPluginData(pluginUrl: string): PluginData | undefined {
    return pluginData[pluginUrl];
  },
};
